// src/context/WorkspaceContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { UserContext } from './UserContext';
import * as WorkspaceService from '../services/WorkspaceService.mjs';
import { setWorkspaceHeader } from '../config/workspaceHeader';

export const WorkspaceContext = createContext({
    workspaces: [],
    activeWorkspace: null,
    activeWorkspaceRole: null,
    setActiveWorkspace: () => {},
    addWorkspace: () => {},
    applyWorkspaceUpdate: () => {},
    isLoading: true,
});

const STORAGE_KEY = 'qdex_active_workspace_id';

export const WorkspaceProvider = ({ children }) => {
    const { user } = useContext(UserContext);
    const [workspaces, setWorkspaces] = useState([]);
    const [activeWorkspaceRole, setActiveWorkspaceRole] = useState(null);
    const [activeWorkspace, setActiveWorkspaceState] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchWorkspaces = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        try {
            const data = await WorkspaceService.getAllWorkspacesForUser();
            setWorkspaces(data);

            const savedId = localStorage.getItem(STORAGE_KEY);
            const saved   = data.find(w => w.id === savedId);
            const initial = saved ?? data.find(w => w.id === user.defaultWorkspaceId) ?? data[0] ?? null;
            setActiveWorkspaceState(initial);
        } catch (err) {
            console.error("Failed to load workspaces:", err);
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        // BUG-9: guard against a missing user, a payload without `members`, or
        // the user no longer being a member of the persisted active workspace.
        // Previously `membershipData.role` threw (no error boundary → white screen).
        if (activeWorkspace && user) {
            const membershipData = activeWorkspace.members?.find(m => m.userId === user.uid);
            setActiveWorkspaceRole(membershipData?.role ?? null);
        } else {
            setActiveWorkspaceRole(null);
        }
        setWorkspaceHeader(activeWorkspace?.id ?? null);
    }, [activeWorkspace, user]);

    useEffect(() => {
        fetchWorkspaces();
    }, [fetchWorkspaces]);

    // fe-core-2: memoize the handlers (and the provider value below) so the
    // context value isn't a brand-new object every render — that forced every
    // consumer of WorkspaceContext to re-render on any unrelated parent render.
    const setActiveWorkspace = useCallback((workspace) => {
        setActiveWorkspaceState(workspace);
        if (workspace?.id) {
            localStorage.setItem(STORAGE_KEY, workspace.id);
        }
    }, []);

    /**
     * Appends a freshly-created workspace to the local list and switches to it.
     * Call this after a successful createWorkspace API response.
     */
    const addWorkspace = useCallback((workspace) => {
        setWorkspaces(prev => [...prev, workspace]);
        setActiveWorkspace(workspace);
    }, [setActiveWorkspace]);

    /**
     * Merges an updated workspace (e.g. after a plan change) into local state,
     * without a full refetch. Updates both the list entry and, if it's the
     * active workspace, the active copy — so screens reading subscriptionTier /
     * maxStorageBytes reflect the change immediately.
     */
    const applyWorkspaceUpdate = useCallback((updated) => {
        if (!updated?.id) return;
        setWorkspaces(prev => prev.map(w => (w.id === updated.id ? { ...w, ...updated } : w)));
        setActiveWorkspaceState(prev =>
            prev && prev.id === updated.id ? { ...prev, ...updated } : prev
        );
    }, []);

    const value = useMemo(() => ({
        workspaces,
        activeWorkspace,
        activeWorkspaceRole,
        setActiveWorkspace,
        addWorkspace,
        applyWorkspaceUpdate,
        isLoading,
    }), [workspaces, activeWorkspace, activeWorkspaceRole, setActiveWorkspace, addWorkspace, applyWorkspaceUpdate, isLoading]);

    return (
        <WorkspaceContext.Provider value={value}>
            {children}
        </WorkspaceContext.Provider>
    );
};
